/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useImperativeHandle,
  cloneElement,
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { DraggableCore } from 'react-draggable';
import classnames from 'classnames';
import useInview from 'utils/inview';
import useKeys from 'utils/keys';
import nextTick from 'utils/nextTick';
import debounce from 'lodash/debounce';
import './Slider.scss';
import styles from './slide.module.scss';

/**
 * This is the Slider component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */

const Slider = forwardRef(
  (
    {
      className,
      children,
      show,
      slide,
      duration,
      inview: inviewSettings,
      onActive,
      onBeforeChange,
      onAfterChange,
      onInview,
    },
    ref
  ) => {
    const container = useRef(null);
    const inner = useRef(null);
    const track = useRef(null);
    const inview = useInview(container, inviewSettings);
    const [draggable, setDraggable] = useState({
      x: 0,
      start: 0,
      cursor: null,
    });
    const [previous, setPrevious] = useState(0);
    const [current, setCurrent] = useState(0);
    const [slides, setSlides] = useState([]);
    const [trackStyle, setTrackStyle] = useState({});
    const [jump, setJump] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
      onInview(inview);
    }, [inview, onInview]);

    useKeys('gallery', inview && children.length > show, {
      left: prev,
      right: next,
    });

    useEffect(() => {
      const cursor = children.length > show ? 'grab' : null;
      setDraggable(draggable => ({
        ...draggable,
        cursor,
      }));
      onActive(children.length > show);
    }, [children, show, onActive]);

    useEffect(() => {
      const items = [...children];
      let pre = [];
      let post = [];

      if (items.length > show) {
        pre = items
          .slice(items.length - (show + 1), items.length)
          .map(child => cloneElement(child, { key: `${child.key}-pre-clone` }));

        post = items
          .slice(0, show + 1)
          .map(child =>
            cloneElement(child, { key: `${child.key}-post-clone` })
          );
      }
      setSlides([...pre, ...items, ...post]);
    }, [show, children]);

    useEffect(() => {
      const trueCurrent = current % children.length;
      const truePrev = previous % children.length;

      if (children.length > show) {
        if (previous === current) {
          onAfterChange(
            trueCurrent < 0
              ? Math.abs(children.length + trueCurrent)
              : Math.abs(trueCurrent)
          );
        } else {
          onBeforeChange(
            truePrev < 0
              ? Math.abs(children.length + truePrev)
              : Math.abs(truePrev),
            trueCurrent < 0
              ? Math.abs(children.length + trueCurrent)
              : Math.abs(trueCurrent)
          );
        }
      }
    }, [previous, current, children, show, onAfterChange, onBeforeChange]);

    useEffect(() => {
      if (slides.length) {
        if (current !== previous) {
          const posX = (100 / slides.length) * (previous - current);
          nextTick(() => {
            if (jump) {
              setTrackStyle(style => ({
                ...style,
                transition: `transform 0.05s ease-in-out`,
                transform: `translateX(${posX}%)`,
              }));
            } else {
              setTrackStyle(style => ({
                ...style,
                transition: `transform ${duration}s ease-in-out`,
                transform: `translateX(${posX}%)`,
              }));
            }
          });
        }
      }
    }, [current, previous, slides, duration, jump]);

    function next(jump = false) {
      if (children.length > show) {
        if (jump === true) {
          setJump(true);
        }
        setPrevious(current);
        setCurrent(current + slide);
      }
    }

    function prev(jump = false) {
      if (children.length > show) {
        if (jump === true) {
          setJump(true);
        }
        setPrevious(current);
        setCurrent(current - slide);
      }
    }

    function goto(index, jump = false) {
      if (children.length > show) {
        const trueCurrent = current % children.length;
        const newCurrent =
          Math.floor(current / children.length) * children.length + index;
        if (index !== trueCurrent) {
          if (jump === true) {
            setJump(true);
          }
          setPrevious(current);
          setCurrent(newCurrent);
        }
      }
    }

    useImperativeHandle(ref, () => ({
      next,
      prev,
      goto,
      getCurrent: () => {
        const trueCurrent = current % children.length;

        return trueCurrent < 0
          ? Math.abs(children.length + trueCurrent)
          : Math.abs(trueCurrent);
      },
    }));

    function onTransitionEnd({ target }) {
      const { current: node } = track;
      if (target === node) {
        const items = [...slides];
        const dist = current - previous;
        let pre = [];
        let post = [];
        if (current > previous) {
          post = items.splice(0, dist);
        } else {
          pre = items.splice(items.length + dist, items.length);
        }

        setJump(false);
        setTrackStyle({});
        setPrevious(current);
        setSlides([...pre, ...items, ...post]);
        setDraggable(draggable => ({
          ...draggable,
          x: 0,
        }));
      }
    }

    function getTrackStyle() {
      if (children.length > show) {
        if (draggable.x !== 0) {
          return {
            width: `${slides.length * (100 / show)}%`,
            marginLeft: `-${100 + 100 / Math.ceil(show)}%`,
            transform: `translateX(${draggable.x}px)`,
            cursor: draggable.cursor,
            ...trackStyle,
          };
        }
        return {
          width: `${slides.length * (100 / show)}%`,
          marginLeft: `-${100 + 100 / Math.ceil(show)}%`,
          cursor: draggable.cursor,
          ...trackStyle,
        };
      }

      return trackStyle;
    }

    function getActive(index) {
      if (children.length > show) {
        const dist = current - previous;
        const start = show + dist;
        const stop = start + show;
        return index > start && index <= stop;
      }

      return true;
    }

    const startDrag = () => {
      setIsDragging(true);
    };

    const endDrag = debounce(() => {
      setIsDragging(false);
    }, 200);

    return (
      <div
        ref={container}
        className={classnames(
          className,
          'slider',
          `slider-show-${show}`,
          `slider-slide-${slide}`,
          {
            'slider-inactive': children.length <= show,
            [styles.isDragging]: isDragging,
          }
        )}
      >
        <div ref={inner} className="slider-inner">
          <DraggableCore
            axis="x"
            disabled={children.length <= show}
            onStart={(e, { x }) => {
              setDraggable(draggable => ({
                ...draggable,
                start: x,
                cursor: 'grabbing',
              }));
            }}
            onDrag={(e, { x }) => {
              const dx = x - draggable.start;

              if (Math.abs(dx) > 20 && !isDragging) {
                startDrag();
              }

              setDraggable(draggable => ({
                ...draggable,
                x: dx,
              }));
            }}
            onStop={() => {
              endDrag();

              setDraggable(draggable => ({
                ...draggable,
                cursor: 'grab',
              }));

              if (draggable.x != 0) {
                if (draggable.x > 0) {
                  prev();
                } else {
                  next();
                }
              }
            }}
          >
            <div
              ref={track}
              className="slider-track"
              style={{
                touchAction: 'pan-y',
                ...getTrackStyle(),
              }}
              onTransitionEnd={onTransitionEnd}
            >
              <TransitionGroup
                key="properties"
                component="div"
                className="slider-track-wrapper"
                appear
              >
                {slides.map((child, index) => (
                  <CSSTransition key={child.key} timeout={600} exit={false}>
                    <div
                      data-index={child.key}
                      className={classnames('slider-slide', styles.child, {
                        active: getActive(index),
                      })}
                      style={{ width: `${100 / slides.length}%` }}
                    >
                      {child}
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </div>
          </DraggableCore>
        </div>
      </div>
    );
  }
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Slider.propTypes = {
  className: PropTypes.string,
  inview: PropTypes.object.isRequired,
  show: PropTypes.number.isRequired,
  slide: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  onActive: PropTypes.func.isRequired,
  onBeforeChange: PropTypes.func.isRequired,
  onAfterChange: PropTypes.func.isRequired,
  onInview: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Slider.defaultProps = {
  inview: {
    threshold: 0.2,
  },
  show: 1,
  slide: 1,
  duration: 0.5,
  children: [],
  onActive: () => {},
  onBeforeChange: () => {},
  onAfterChange: () => {},
  onInview: () => {},
};

export default Slider;
